<template>
  <div class="d-flex flex-column-fluid business-insights-page">
    <div class="container">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="header-title">
            <h1 class="title">{{ businessName }}</h1>
            <p class="subtitle">Track and manage financing overview for this business</p>
          </div>
          <div class="header-actions">
            <button class="btn btn-dark btn-dashboard" @click="goBack">
              <i class="flaticon2-line-chart"></i>
              Back
            </button>
          </div>
        </div>
      </div>
      
      <div class="business-insights-content">
      <!-- Top Section: Credit Limit and KPI Grid (50/50) -->
      <section class="top-section">
        <!-- Financing Limit Bar -->
        <div class="credit-limit-wrapper">
          <financing-limit-bar
            title="Credit Limit"
            :total="total"
            :used="used"
            :pending="pending"
            :available="available"
            :currency="currency"
            used-label="Used Limit"
            pending-label="Under Review"
            available-label="Available"
            title-color="#333333"
            :has-card-styling="true"
            font-family="Light"
            width="100%"
          />
        </div>
        
        <!-- KPI Grid -->
        <div class="kpi-grid-wrapper">
          <div class="kpi-grid">
            <div class="kpi-card">
              <div class="kpi-value">{{ formatNumber(aggregateData.total_invoices) }}</div>
              <div class="kpi-label">Total Financing Requests</div>
            </div>
            <div class="kpi-card">
              <div class="kpi-value">{{ formatCurrencyCompact(aggregateData.total_used_amount) }}<sup>{{ currency }}</sup></div>
              <div class="kpi-label">Total Used Amount</div>
            </div>
            <div class="kpi-card">
              <div class="kpi-value">{{ formatCurrencyCompact(aggregateData.total_repayments_due) }}<sup>{{ currency }}</sup></div>
              <div class="kpi-label">Total Repayment & Fees Due</div>
            </div>
            <div class="kpi-card">
              <div class="kpi-value">{{ pendingReviewCount }}</div>
              <div class="kpi-label">Pending Review</div>
            </div>
          </div>
        </div>
      </section>

      <!-- Bottom Section: Repayments and Payers (50/50) -->
      <section class="bottom-section">
        <!-- Upcoming Repayments & Fees -->
        <div class="table-card repayments-card">
          <div class="card-header">
            <h3 class="card-title">Upcoming Repayments & Fees <span class="count-badge">{{ formatNumber(repaymentsCount) }}</span></h3>
            <button class="view-all-btn" @click="viewAllRepayments">MORE</button>
          </div>
          
          <div v-if="loadingRepayments" class="loading-state">
            <div class="spinner"></div>
            <p>Loading repayments...</p>
          </div>
          
          <div v-else-if="recentRepayments.length === 0" class="empty-state">
            <i class="las la-file-invoice"></i>
            <p>No repayments found</p>
          </div>
          
          <div v-else class="table-content">
            <table class="data-table">
              <thead>
                <tr>
                  <th>INVOICE REFERENCE</th>
                  <th>DUE DATE</th>
                  <th>AMOUNT DUE</th>
                  <th>STATUS</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="repayment in recentRepayments" :key="repayment.id">
                  <td>{{ repayment.invoice_reference }}</td>
                  <td>{{ formatDate(repayment.due_date) }}</td>
                  <td>{{ repayment.amount_due !== null ? formatNumberCompact(repayment.amount_due, repayment.currency || repaymentsCurrency) : 'No amount due' }}<sup v-if="repayment.amount_due !== null">{{ repayment.currency || repaymentsCurrency }}</sup></td>
                  <td><span :class="['status-badge', statusBadgeClass(repayment.due_status)]">{{ (repayment.due_status || 'UNKNOWN').toUpperCase() }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Eligible Payers -->
        <div class="table-card payers-card">
          <div class="card-header">
            <h3 class="card-title">Eligible Payers <span class="count-badge">{{ formatNumber(eligibleCustomersCount) }}</span></h3>
            <button class="view-all-btn" @click="viewAllCustomers">MORE</button>
          </div>
          
          <div v-if="loadingCustomers" class="loading-state">
            <div class="spinner"></div>
            <p>Loading payers...</p>
          </div>
          
          <div v-else-if="eligibleCustomers.length === 0" class="empty-state">
            <i class="las la-users"></i>
            <p>No eligible payers found</p>
          </div>
          
          <div v-else class="table-content">
            <table class="data-table">
              <thead>
                <tr>
                  <th>PAYER</th>
                  <th>TOTAL DISBURSED AMOUNT</th>
                  <th>AVAILABLE CREDIT</th>
                  <th>STATUS</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="customer in recentCustomers" :key="customer.id">
                  <td>{{ customer.customer_name || customer.id }}</td>
                  <td>{{ formatNumberCompact(customer.total_disbursed_amount, customer.base_currency) }}<sup>{{ customer.base_currency }}</sup></td>
                  <td>{{ formatNumberCompact(customer.pending_balance || 0, customer.base_currency) }}<sup>{{ customer.base_currency }}</sup></td>
                  <td><span :class="['status-badge', customer.is_active ? 'status-active' : 'status-inactive']">{{ customer.is_active ? 'ACTIVE' : 'INACTIVE' }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- Monthly Disbursed Amount Chart Section -->
      <section class="monthly-chart-section">
        <div class="chart-card monthly-chart">
          <h3 class="chart-title">Monthly Disbursed Amount</h3>
          <GenericChart
            v-if="monthlyDisbursedXAxis.length > 0"
            :xAxis="monthlyDisbursedXAxis"
            :series="monthlyDisbursedSeries"
            :colors="monthlyDisbursedColors"
            type="bar"
            yAxis="Amount"
            :showXAxisLabels="true"
          />
          <div v-else class="chart-placeholder">
            <p>No monthly disbursement data available</p>
          </div>
        </div>
      </section>

      <!-- Recent Activity Section -->
      <section v-if="invoiceRequests && invoiceRequests.length" class="activity-section">
        <div class="table-card">
          <div class="card-header">
            <h3 class="card-title">Financing Invoices</h3>
            <button class="view-all-btn" @click="viewAllRequests">MORE</button>
          </div>
          <div class="activity-table-wrapper">
            <table class="activity-table">
            <thead>
              <tr>
                <th class="invoice-number-header">INVOICE NUMBER</th>
                <th class="amount-header">TOTAL PURCHASED<br/>RECEIVABLE AMOUNT</th>
                <th class="amount-header">TOTAL FEES</th>
                <th class="amount-header">TOTAL DISBURSED<br/>AMOUNT</th>
                <th class="status-header">STATUS</th>
                <th class="invoice-header">INVOICE</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="req in invoiceRequests" :key="req.id" class="activity-row clickable-row" @click="openFinanceRequestModal(req.id)">
                <td class="reference-cell">{{ req.invoice_reference }}</td>
                <td class="amount-cell">{{ formatFullNumber(req.total_receivable_amount) }}<sup>{{ req.currency }}</sup></td>
                <td class="amount-cell">{{ formatFullNumber(req.total_fees) }}<sup>{{ req.currency }}</sup></td>
                <td class="amount-cell">{{ formatFullNumber(req.disbursement_amount) }}<sup>{{ req.currency }}</sup></td>
                <td class="status-cell">
                  <span :class="['status-badge', statusBadgeClass(req.status)]">{{ req.status }}</span>
                </td>
                <td class="invoice-cell">
                  <button 
                    v-if="req.file" 
                    class="invoice-download-btn" 
                    @click.stop="viewInvoice(req.file, req.invoice_reference)"
                    title="View Invoice PDF"
                  >
                    <i class="las la-file-pdf"></i>
                  </button>
                  <span v-else class="no-invoice">-</span>
                </td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>
      </section>
      
      <div v-else class="empty-activity">
        <i class="las la-chart-line"></i>
        <p>No recent activity found.</p>
      </div>
    </div>

    <!-- Finance Request Modal -->
    <FinanceRequestModal ref="financeRequestModal" :currency="currency" @updated="onFinanceRequestUpdated" />

    <!-- PDF Viewer -->
    <pdf-viewer 
      v-if="showPDF" 
      :url="pdfUrl" 
      :name="pdfName"
      :showHeader="true"
      :forceExpansion="true"
      :enableOverlayEffect="true"
      @closePDF="closePDFViewer"
    />
    </div>
  </div>
</template>

<script>
import FinancingService from '@/services/financing.service';
import CompaniesService from '@/services/companies.service';
import GenericChart from '@/components/financing/GenericChart.vue';
import FinancingLimitBar from '@/components/financing/FinancingLimitBar.vue';
import FinanceRequestModal from './components/FinanceRequestModal.vue';
import PdfViewer from '@/components/pdfViewer.vue';
import { formatCurrencyCompact, formatNumberCompact, formatNumber as formatNumberUtil } from '@/utils/number-formatter.js';

const statusMap = {
  PENDING_REVIEW: { label: 'Pending Review', color: '#ffc32b' },
  APPROVED: { label: 'Approved', color: '#22d06c' },
  REJECTED: { label: 'Rejected', color: '#e74c3c' },
  DISBURSED_AMOUNT: { label: 'Disbursed Amount', color: '#8e44ad' },
  CLOSED: { label: 'Closed', color: '#888' },
  PARTIALLY_PAID: { label: 'Partially Paid', color: '#ffa500' },
  DUE: { label: 'Due', color: '#fef3c7' },
};

export default {
  name: 'BusinessInsights',
  components: {
    GenericChart,
    FinancingLimitBar,
    FinanceRequestModal,
    PdfViewer
  },
  data() {
    return {
      businessId: null,
      businessName: 'Loading...',
      pendingReviewCount: 0,
      aggregateData: {
        limit: 0,
        balance: 0,
        pending_balance: 0,
        total_repayments_due: 0,
        total_used_amount: 0,
        total_invoices: 0,
        currency: 'AED',
        count_by_status: {},
        invoices_requests: []
      },
      invoiceRequests: [],
      expandedRequest: null,
      currency: 'AED',
      chartXAxis: [],
      chartSeries: [],
      chartColors: [],
      eligibleCustomers: [],
      loadingCustomers: false,
      showPDF: false,
      pdfUrl: null,
      pdfName: '',
      repayments: [],
      loadingRepayments: false,
      repaymentsCurrency: 'AED',
      monthlyDisbursedXAxis: [],
      monthlyDisbursedSeries: [],
      monthlyDisbursedColors: [
        '#2774E9', '#22d06c', '#ffc32b', '#e67e22', '#04CD68', '#1a5bb8', '#8e44ad', '#e74c3c'
      ],
      repaymentsCount: 0,
      eligibleCustomersCount: 0,
    };
  },
  computed: {
    currentBusinessId() {
      return this.businessId || this.$route.params.business_id;
    },
    total() {
      const value = parseFloat(this.aggregateData.limit) || 0;
      return value;
    },
    used() {
      const value = parseFloat(this.aggregateData.available) || 0;
      return value;
    },
    pending() {
      const value = parseFloat(this.aggregateData.pending_balance) || 0;
      return value;
    },
    available() {
      const value = parseFloat(this.aggregateData.balance) || 0;
      return value;
    },
    recentCustomers() {
      return this.eligibleCustomers;
    },
    recentRepayments() {
      return this.repayments;
    },
  },
  methods: {
    formatCurrency(val, currencyOverride) {
      if (val === undefined || val === null || isNaN(val)) return '-';
      const currency = currencyOverride || this.currency;
      return Number(val).toLocaleString(undefined, { style: 'currency', currency });
    },
    formatCurrencyCompact(val, currencyOverride) {
      return formatCurrencyCompact(val, currencyOverride || this.currency);
    },
    formatNumberCompact(val) {
      return formatNumberCompact(val);
    },
    formatNumber(val) {
      return formatNumberUtil(val);
    },
    formatFullNumber(val) {
      if (val === undefined || val === null || isNaN(val)) return '-';
      return Number(val).toLocaleString('en-US', { 
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    statusBadgeClass(status) {
      if (!status) return 'status-default';
      const s = status.toLowerCase();
      
      // Financing request statuses
      if (s === 'approved' || s === 'funded') return 'status-approved';
      if (s === 'pending') return 'status-pending';
      if (s === 'rejected') return 'status-rejected';
      if (s === 'processing') return 'status-processing';
      if (s === 'pending_review') return 'status-pending_review';
      if (s === 'disbursed_amount') return 'status-disbursed_amount';
      if (s === 'closed') return 'status-closed';
      if (s === 'due') return 'status-due';
      
      // Repayment statuses (matching table colors)
      if (s === 'due') return 'status-due';
      if (s === 'overdue') return 'status-overdue';
      if (s === 'paid') return 'status-paid';
      if (s === 'due_soon') return 'status-due-soon';
      if (s === 'partially_paid') return 'status-partially-paid';
      
      // fallback: try to generate a class for any status
      return 'status-' + s.replace(/[^a-z0-9_-]/g, '') || 'status-default';
    },
    toggleExpand(id) {
      this.expandedRequest = this.expandedRequest === id ? null : id;
    },
    formatDate(dateString) {
      if (!dateString || dateString === null) return 'No due date';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    async loadBusinessData() {
      try {
        // Load business details for name
        const businessResponse = await CompaniesService.get(this.businessId, {"list_view": true});
        if (businessResponse && businessResponse.data) {
          this.businessName = businessResponse.data.name || `Business ${this.businessId}`;
        }

        // Load aggregate financing data (this contains all the metrics we need)
        const aggregateResponse = await FinancingService.getFinancingAggregate(this.businessId);
        
        if (aggregateResponse && aggregateResponse.data) {
          const data = aggregateResponse.data;
          
          // Directly assign the response data
          this.aggregateData = {
            limit: data.limit || 0,
            balance: data.balance || 0,
            pending_balance: data.pending_balance || 0,
            total_repayments_due: data.total_repayments_due || 0,
            total_used_amount: data.total_used_amount || 0,
            total_invoices: data.total_invoices || 0,
            currency: data.currency || 'AED',
            count_by_status: data.count_by_status || {},
            invoices_requests: data.invoices_requests || [],
            available: data.available || 0
          };
          
          // Update component-level properties
          this.currency = data.currency || 'AED';
          this.invoiceRequests = data.invoices_requests || [];
          this.pendingReviewCount = data.count_by_status?.PENDING_REVIEW || 0;
          
          
          // Prepare chart data from count_by_status
          this.prepareChartData();
        } else {
          console.warn('No aggregate data received');
        }
      } catch (error) {
        console.error('Error loading business data:', error);
        this.$showAlert('Failed to load business data', 'danger');
      }
    },

    prepareChartData() {
      const countByStatus = this.aggregateData.count_by_status || {};
      this.chartXAxis = [];
      this.chartColors = [];
      const chartData = [];
      const labelToData = new Map();
      
      // First pass: collect all data for each unique label
      for (const key in countByStatus) {
        if (Object.prototype.hasOwnProperty.call(countByStatus, key)) {
          const map = statusMap[key] || { label: key, color: '#bfc9da' };
          const label = map.label;
          
          if (labelToData.has(label)) {
            labelToData.get(label).count += countByStatus[key];
          } else {
            labelToData.set(label, {
              count: countByStatus[key],
              color: map.color
            });
          }
        }
      }
      
      // Second pass: build arrays for chart
      for (const [label, data] of labelToData) {
        this.chartXAxis.push(label);
        chartData.push(data.count);
        this.chartColors.push(data.color);
      }
      
      // For pie chart, we need to provide the data directly as array values
      // For other chart types, we use the series format
      this.chartSeries = chartData;
    },
    async loadEligibleCustomers() {
      try {
        this.loadingCustomers = true;
        // Use the dedicated eligible customers API
        const response = await FinancingService.getEligibleCustomers(this.businessId, {"page": 1, "page_size": 5});
        if (response && response.data) {
          this.eligibleCustomersCount = response.count;
          this.eligibleCustomers = response.data.map(customer => {
            const isGroup = !!customer.customer_group__id;
            const payerId = isGroup ? customer.customer_group__id : customer.customer_id;
            const payerName = isGroup ? customer.customer_group_name : customer.customer_name;
            
            return {
              id: payerId,
              customer_name: payerName || (isGroup ? `Unknown Group ${payerId}` : `Unknown Customer ${payerId}`),
              base_currency: customer.base_currency,
              is_active: customer.is_active !== false, // Default to true if not specified
              total_disbursed_amount: customer.total_disbursed_amount || 0,
              available_credit: customer.available_credit || 0,
              pending_balance: customer.pending_balance || 0,
              type: isGroup ? 'group' : 'customer'
            };
          });
        } else {
          this.eligibleCustomers = [];
        }
      } catch (error) {
        console.error('Error loading eligible customers:', error);
        this.eligibleCustomers = [];
      } finally {
        this.loadingCustomers = false;
      }
    },




    async loadRepayments() {
      try {
        this.loadingRepayments = true;
        const response = await FinancingService.getRepayments(this.businessId, {"page": 1, "page_size": 3});
        if (response && response.data && response.data) {
          this.repaymentsCount = response.count;
          this.repayments = response.data;
        } else {
          this.repayments = [];
        }
        
        if (response.data?.currency) {
          this.repaymentsCurrency = response.data.currency;
        }
      } catch (error) {
        console.error('Error loading repayments:', error);
        this.repayments = [];
      } finally {
        this.loadingRepayments = false;
      }
    },
    
    openFinanceRequestModal(requestId) {
      this.$refs.financeRequestModal.open(requestId);
    },
    
    openFinanceRequestModalByReference(refNum) {
      // Find the finance request by invoice reference
      const financeRequest = this.invoiceRequests.find(
        request => request.invoice_reference === refNum
      );
      
      if (financeRequest) {
        this.openFinanceRequestModal(financeRequest.id);
        // Clean URL after opening modal
        this.$router.replace({
          path: this.$route.path,
          query: { ...this.$route.query, ref_num: undefined }
        });
      } else {
        console.warn(`Finance request with reference ${refNum} not found`);
        this.$showAlert(`Finance request with reference ${refNum} not found`, 'warning');
      }
    },
    
    checkUrlParameters() {
      const refNum = this.$route.query.ref_num;
      if (refNum) {
        // Wait a bit for data to load before trying to open modal
        this.$nextTick(() => {
          setTimeout(() => {
            this.openFinanceRequestModalByReference(refNum);
          }, 1000); // Give time for invoiceRequests to populate
        });
      }
    },
    
    async onFinanceRequestUpdated(requestId) {
      // Refresh all data to show updated information across all components
      await Promise.allSettled([
        this.loadBusinessData(),
        this.loadEligibleCustomers(),
        this.loadRepayments(),
        FinancingService.getMonthlyDisbursedAnalytics(this.businessId).then(response => {
          try {
            const analytics = response.data;
            this.monthlyDisbursedXAxis = analytics.map(item => item.month);
            this.monthlyDisbursedSeries = [{ name: 'Disbursed', data: analytics.map(item => item.monthly_disbursed_amount) }];
          } catch (error) {
            console.error('Error processing monthly analytics:', error);
            this.monthlyDisbursedXAxis = [];
            this.monthlyDisbursedSeries = [];
          }
        }).catch(error => {
          console.error('Error loading monthly analytics:', error);
          this.monthlyDisbursedXAxis = [];
          this.monthlyDisbursedSeries = [];
        })
      ]);
    },
    
    viewAllRepayments() {
      const businessId = this.currentBusinessId;
      if (!businessId) {
        this.$showAlert('Business ID is not available', 'danger');
        return;
      }
      // Navigate to outstanding repayments & fees page with business ID
      this.$router.push({
        name: 'upcoming-repayments',
        query: { business_id: businessId }
      });
    },
    
    viewAllCustomers() {
      const businessId = this.currentBusinessId;
      if (!businessId) {
        this.$showAlert('Business ID is not available', 'danger');
        return;
      }
      // Navigate to eligible customers page with business ID
      this.$router.push({
        name: 'eligible-customers',
        query: { business_id: businessId }
      });
    },

    goBack() {
      // Navigate back to the businesses list
      this.$router.push({ name: 'financing' });
    },

    viewAllRequests() {
      const businessId = this.currentBusinessId;
      if (!businessId) {
        this.$showAlert('Business ID is not available', 'danger');
        return;
      }
      this.$router.push({
        name: 'financing-requests',
        params: { business_id: businessId }
      });
    },
    
    async viewInvoice(fileUrl, invoiceReference) {
      if (!fileUrl) {
        this.$showAlert('No file available to view', 'warning');
        return;
      }
      
      try {
        // Fetch the PDF as a blob to avoid CORS issues
        const response = await fetch(fileUrl, { method: 'GET' });
        const blob = await response.blob();
        
        // Create a blob URL
        this.pdfUrl = URL.createObjectURL(blob);
        this.pdfName = `${invoiceReference}-invoice.pdf`;
        this.showPDF = true;
      } catch (error) {
        console.error('Error viewing invoice:', error);
        this.$showAlert('Failed to view invoice', 'danger');
      }
    },
    
    closePDFViewer() {
      this.showPDF = false;
      // Clean up the blob URL to free memory
      if (this.pdfUrl) {
        URL.revokeObjectURL(this.pdfUrl);
        this.pdfUrl = null;
      }
      this.pdfName = '';
    },
  },
  watch: {
    repayments: {
      handler(newVal) {
      },
      deep: true
    }
  },
  async mounted() {
    this.businessId = this.$route.params.business_id;
    
    if (!this.businessId) {
      this.$showAlert('Business ID is required', 'danger');
      this.$router.push('/financing/businesses');
      return;
    }

    // Execute all API calls in parallel using Promise.allSettled
    const [
      businessDataResult,
      eligibleCustomersResult,
      repaymentsResult,
      monthlyAnalyticsResult,
    ] = await Promise.allSettled([
      this.loadBusinessData(),
      this.loadEligibleCustomers(),
      this.loadRepayments(),
      FinancingService.getMonthlyDisbursedAnalytics(this.businessId),
    ]);
    
    // Check URL parameters after data is loaded
    this.checkUrlParameters();

    // Handle monthly analytics data
    if (monthlyAnalyticsResult.status === 'fulfilled') {
      try {
        const analytics = monthlyAnalyticsResult.value.data;
        this.monthlyDisbursedXAxis = analytics.map(item => item.month);
        this.monthlyDisbursedSeries = [{ name: 'Disbursed', data: analytics.map(item => item.monthly_disbursed_amount) }];
      } catch (error) {
        console.error('Error processing monthly analytics:', error);
        this.monthlyDisbursedXAxis = [];
        this.monthlyDisbursedSeries = [];
      }
    } else {
      // Handle monthly analytics error (fail silently)
      console.error('Error loading monthly analytics:', monthlyAnalyticsResult.reason);
      this.monthlyDisbursedXAxis = [];
      this.monthlyDisbursedSeries = [];
    }

    // Log any errors from other API calls (they handle their own data internally)
    if (businessDataResult.status === 'rejected') {
      console.error('Error loading business data:', businessDataResult.reason);
    }
    if (eligibleCustomersResult.status === 'rejected') {
      console.error('Error loading eligible customers:', eligibleCustomersResult.reason);
    }
    if (repaymentsResult.status === 'rejected') {
      console.error('Error loading repayments:', repaymentsResult.reason);
    }
  },
  watch: {
    '$route.query.ref_num': {
      handler(newRefNum) {
        if (newRefNum && this.invoiceRequests.length > 0) {
          this.openFinanceRequestModalByReference(newRefNum);
        }
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "./BusinessInsights.scss";
</style> 